import React, { useCallback, useEffect, useState } from "react";
import {
    Loader2, Inbox, Filter, Building2, ShieldCheck, AlertTriangle, ChevronRight,
    CheckCircle2, XCircle, LayoutGrid, Rows3,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import KycDocumentsPanel from "@/components/kyc/KycDocumentsPanel";
import TableToolbar from "@/components/shared/TableToolbar";
import TablePagination from "@/components/shared/TablePagination";
import { adminKycApi } from "@/lib/api/kyc";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

function StatusBadge({ status, t }) {
    const tone = status === "APPROVED" ? "border-success/40 text-success bg-success/5"
              : status === "PENDING"  ? "border-warning/40 text-warning bg-warning/5"
              : status === "REJECTED" ? "border-destructive/40 text-destructive bg-destructive/5"
              : "border-border text-muted-foreground";
    return <Badge variant="outline" className={tone}>{t(`offers.status.${status}`)}</Badge>;
}

function ViewToggle({ value, onChange, t }) {
    const opts = [
        { v: "table", Icon: Rows3, label: t("table.viewTable") },
        { v: "grid",  Icon: LayoutGrid, label: t("table.viewGrid") },
    ];
    return (
        <div className="inline-flex items-center rounded-md border border-border p-0.5" data-testid="kyc-view-toggle">
            {opts.map(({ v, Icon, label }) => (
                <button
                    key={v}
                    type="button"
                    onClick={() => onChange(v)}
                    aria-label={label}
                    title={label}
                    className={`h-8 w-8 inline-flex items-center justify-center rounded-sm transition-colors ${
                        value === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`kyc-view-${v}`}
                >
                    <Icon className="h-4 w-4" />
                </button>
            ))}
        </div>
    );
}

function TableRowKyc({ row, onOpen, t }) {
    return (
        <tr
            className="border-t border-border hover:bg-muted/20 cursor-pointer"
            onClick={() => onOpen(row.id)}
            data-testid={`kyc-table-row-${row.id}`}
        >
            <td className="px-5 py-3">
                <div className="flex items-start gap-2.5">
                    <div className="icon-tile h-9 w-9 shrink-0 overflow-hidden">
                        {row.logo_url
                            ? <img src={row.logo_url} alt="" className="h-full w-full object-cover" />
                            : <Building2 className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold truncate flex items-center gap-2">
                            {row.corporate_name}
                            {row.first_kyc && row.kyc_status === "PENDING" && (
                                <Badge variant="outline" className="text-[10px]">{t("kyc.firstSubmission")}</Badge>
                            )}
                        </div>
                        <div className="text-xs text-muted-foreground">{t(`provider.types.${row.type}`)}</div>
                    </div>
                </div>
            </td>
            <td className="px-5 py-3"><StatusBadge status={row.kyc_status} t={t} /></td>
            <td className="px-5 py-3 text-sm">
                {row.kyc_submitted_at
                    ? new Date(row.kyc_submitted_at).toLocaleDateString()
                    : <span className="text-muted-foreground">—</span>}
            </td>
            <td className="px-5 py-3 text-sm">
                {row.license_expiry || <span className="text-muted-foreground">—</span>}
                {row.license_expired ? (
                    <div className="inline-flex items-center gap-1 text-[11px] text-destructive ms-2">
                        <AlertTriangle className="h-3 w-3" />
                        {t("kyc.licenseExpiredOn", { date: row.license_expiry })}
                    </div>
                ) : row.license_days_to_expiry !== null && row.license_days_to_expiry !== undefined && row.license_days_to_expiry <= 30 ? (
                    <div className="inline-flex items-center gap-1 text-[11px] text-warning ms-2">
                        <AlertTriangle className="h-3 w-3" />
                        {t("admin.providers.licenseExpiring", { days: row.license_days_to_expiry })}
                    </div>
                ) : null}
            </td>
            <td className="px-5 py-3 text-sm">
                {row.missing_required?.length > 0 ? (
                    <span className="text-warning">{t("kyc.missingDocs", { n: row.missing_required.length })}</span>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </td>
            <td className="px-5 py-3 text-end">
                <ChevronRight className="inline h-4 w-4 text-muted-foreground" />
            </td>
        </tr>
    );
}

function Row({ row, onOpen, t }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(row.id)}
            className="surface-card p-4 w-full text-start hover:border-primary/40 transition-colors"
            data-testid={`kyc-queue-row-${row.id}`}
        >
            <div className="flex items-start gap-3">
                <div className="icon-tile h-10 w-10 shrink-0 overflow-hidden">
                    {row.logo_url
                        ? <img src={row.logo_url} alt="" className="h-full w-full object-cover" />
                        : <Building2 className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold truncate flex-1 min-w-0">{row.corporate_name}</h3>
                        <StatusBadge status={row.kyc_status} t={t} />
                        {row.first_kyc && row.kyc_status === "PENDING" && (
                            <Badge variant="outline" className="text-[10px]">{t("kyc.firstSubmission")}</Badge>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{t(`provider.types.${row.type}`)}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                        {row.kyc_submitted_at && <span>{t("kyc.submitted")}: {new Date(row.kyc_submitted_at).toLocaleDateString()}</span>}
                        {row.missing_required?.length > 0 && (
                            <span className="text-warning">
                                {t("kyc.missingDocs", { n: row.missing_required.length })}
                            </span>
                        )}
                        {row.license_expired && (
                            <span className="inline-flex items-center gap-1 text-destructive">
                                <AlertTriangle className="h-3 w-3" />
                                {t("kyc.licenseExpiredOn", { date: row.license_expiry })}
                            </span>
                        )}
                        {!row.license_expired && row.license_days_to_expiry !== null && row.license_days_to_expiry !== undefined && row.license_days_to_expiry <= 30 && (
                            <span className="inline-flex items-center gap-1 text-warning">
                                <AlertTriangle className="h-3 w-3" />
                                {t("admin.providers.licenseExpiring", { days: row.license_days_to_expiry })}
                            </span>
                        )}
                    </div>
                    {row.kyc_rejection_reason && (
                        <div className="mt-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-xs text-destructive line-clamp-2">
                            <strong>{t("offers.rejectionReason")}: </strong>{row.kyc_rejection_reason}
                        </div>
                    )}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
        </button>
    );
}

export default function KycQueuePage() {
    const { t, isRTL } = useI18n();
    const [tab, setTab] = useState("queue");
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [activeId, setActiveId] = useState(null);
    const [filterValues, setFilterValues] = useState({ status: "PENDING" });
    const [sortValue, setSortValue] = useState("submitted_desc");
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem("kyc_view_mode") || "table");

    useEffect(() => { localStorage.setItem("kyc_view_mode", viewMode); }, [viewMode]);

    const load = useCallback(async () => {
        try {
            const params = tab === "expiring"
                ? { expiring_soon: true, limit: pageSize, skip: (page - 1) * pageSize }
                : {
                    status: filterValues.status || undefined,
                    sort: sortValue || undefined,
                    limit: pageSize,
                    skip: (page - 1) * pageSize,
                };
            const res = await adminKycApi.queue(params);
            setItems(res.items || []);
            setTotal(res.total ?? 0);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    }, [tab, filterValues, sortValue, pageSize, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [tab, filterValues, sortValue, pageSize]);

    return (
        <div className="max-w-7xl space-y-6 fade-up" data-testid="page-admin-kyc">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("kyc.adminTitle")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{t("kyc.adminSubtitle")}</p>
                </div>
            </header>

            <Tabs value={tab} onValueChange={setTab}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <TabsList>
                        <TabsTrigger value="queue" data-testid="kyc-tab-queue">
                            <ShieldCheck className="h-4 w-4 me-1" />
                            {t("kyc.tabs.queue")}
                        </TabsTrigger>
                        <TabsTrigger value="expiring" data-testid="kyc-tab-expiring">
                            <AlertTriangle className="h-4 w-4 me-1" />
                            {t("kyc.tabs.expiring")}
                        </TabsTrigger>
                    </TabsList>
                </div>
                <div className="mt-3">
                    <TableToolbar
                        filters={tab === "queue" ? [
                            {
                                key: "status",
                                label: t("admin.providers.cols.kyc"),
                                options: ["DRAFT", "PENDING", "APPROVED", "REJECTED"].map((s) => ({
                                    value: s,
                                    label: t(`offers.status.${s}`),
                                })),
                            },
                        ] : undefined}
                        filterValues={tab === "queue" ? filterValues : undefined}
                        onFilterChange={tab === "queue" ? ((k, v) => setFilterValues((p) => ({ ...p, [k]: v }))) : undefined}
                        onClearFilters={tab === "queue" ? (() => setFilterValues({})) : undefined}
                        sortOptions={tab === "queue" ? [
                            { value: "submitted_desc", label: t("table.sort.submittedDesc") },
                            { value: "submitted_asc", label: t("table.sort.submittedAsc") },
                            { value: "name_asc", label: t("table.sort.nameAsc") },
                            { value: "name_desc", label: t("table.sort.nameDesc") },
                        ] : undefined}
                        sortValue={tab === "queue" ? sortValue : undefined}
                        onSortChange={tab === "queue" ? setSortValue : undefined}
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                        rightSlot={<ViewToggle value={viewMode} onChange={setViewMode} t={t} />}
                    />
                </div>

                <TabsContent value="queue" className="mt-4">
                    {items === null ? (
                        <div className="surface-card p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                    ) : items.length === 0 ? (
                        <div className="surface-card p-10 text-center" data-testid="kyc-queue-empty">
                            <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                            <p className="text-sm font-medium">{t("kyc.queueEmpty")}</p>
                        </div>
                    ) : viewMode === "table" ? (
                        <div className="surface-card overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="kyc-queue-table">
                                    <thead className="bg-muted/30">
                                        <tr>
                                            <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.provider")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.kyc")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("kyc.submitted")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("kyc.licenseExpiry")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("kyc.missingDocsHeader")}</th>
                                            <th className="px-5 py-3 font-semibold text-end" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((row) => <TableRowKyc key={row.id} row={row} onOpen={setActiveId} t={t} />)}
                                    </tbody>
                                </table>
                            </div>
                            <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                {items.map((row) => <Row key={row.id} row={row} onOpen={setActiveId} t={t} />)}
                            </div>
                            <div className="surface-card mt-4">
                                <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
                            </div>
                        </>
                    )}
                </TabsContent>

                <TabsContent value="expiring" className="mt-4">
                    {items === null ? (
                        <div className="surface-card p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                    ) : items.length === 0 ? (
                        <div className="surface-card p-10 text-center" data-testid="kyc-expiring-empty">
                            <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                            <p className="text-sm font-medium">{t("kyc.expiringEmpty")}</p>
                        </div>
                    ) : viewMode === "table" ? (
                        <div className="surface-card overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="kyc-expiring-table">
                                    <thead className="bg-muted/30">
                                        <tr>
                                            <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.provider")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("admin.providers.cols.kyc")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("kyc.submitted")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("kyc.licenseExpiry")}</th>
                                            <th className="text-start px-5 py-3 font-semibold">{t("kyc.missingDocsHeader")}</th>
                                            <th className="px-5 py-3 font-semibold text-end" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((row) => <TableRowKyc key={row.id} row={row} onOpen={setActiveId} t={t} />)}
                                    </tbody>
                                </table>
                            </div>
                            <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                {items.map((row) => <Row key={row.id} row={row} onOpen={setActiveId} t={t} />)}
                            </div>
                            <div className="surface-card mt-4">
                                <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
                            </div>
                        </>
                    )}
                </TabsContent>
            </Tabs>

            <KycReviewSheet
                providerId={activeId}
                isRTL={isRTL}
                t={t}
                onClose={() => setActiveId(null)}
                onChanged={load}
            />
        </div>
    );
}

function KycReviewSheet({ providerId, isRTL, t, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [decision, setDecision] = useState({ open: false, action: null });

    const open = !!providerId;

    const refresh = useCallback(async () => {
        if (!providerId) return;
        setLoading(true);
        try {
            const summary = await adminKycApi.summary(providerId);
            setData(summary);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [providerId]);

    useEffect(() => {
        if (open) { setData(null); refresh(); }
    }, [open, refresh]);

    const handleOpenChange = (o) => {
        if (!o) { onClose?.(); onChanged?.(); }
    };

    const upload = async (payload) => {
        const doc = await adminKycApi.upload(providerId, payload);
        await refresh();
        return doc;
    };
    const remove = async (id) => {
        await adminKycApi.remove(providerId, id);
        await refresh();
    };

    const decide = async (action, reason) => {
        try {
            await adminKycApi.decide(providerId, {
                decision: action,
                rejection_reason: action === "REJECTED" ? (reason || "").trim() : null,
            });
            toast.success(t(action === "APPROVED" ? "kyc.toasts.approved" : "kyc.toasts.rejected"));
            setDecision({ open: false, action: null });
            // Close the sheet (which also calls onChanged → load() to refetch the queue).
            onClose?.();
            onChanged?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    const p = data?.provider;
    const isPending = p?.kyc_status === "PENDING";

    return (
        <>
            <Sheet open={open} onOpenChange={handleOpenChange}>
                <SheetContent
                    side={isRTL ? "left" : "right"}
                    className="w-full sm:max-w-2xl overflow-y-auto"
                    data-testid="kyc-review-sheet"
                    dir={isRTL ? "rtl" : "ltr"}
                >
                    <SheetHeader className="text-start">
                        <div className="flex items-start gap-3">
                            <div className="icon-tile h-12 w-12 shrink-0 overflow-hidden">
                                {p?.logo_url
                                    ? <img src={p.logo_url} alt="" className="h-full w-full object-cover" />
                                    : <Building2 className="h-6 w-6" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <SheetTitle className="text-xl truncate" data-testid="kyc-review-name">
                                    {p?.corporate_name || "—"}
                                </SheetTitle>
                                <SheetDescription className="sr-only">
                                    {t("kyc.adminTitle")}
                                </SheetDescription>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                    {p && <StatusBadge status={p.kyc_status} t={t} />}
                                    {p?.is_visible
                                        ? <Badge variant="outline" className="border-success/40 text-success">{t("admin.providers.visible")}</Badge>
                                        : p && <Badge variant="secondary">{t("admin.providers.hidden")}</Badge>}
                                    {p?.kyc_submitted_at && (
                                        <span className="text-[11px] text-muted-foreground">
                                            {t("kyc.submittedAt", { date: new Date(p.kyc_submitted_at).toLocaleString() })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </SheetHeader>

                    {loading && !data ? (
                        <div className="py-20 flex items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                    ) : data ? (
                        <div className="mt-5 space-y-4">
                            {p.kyc_rejection_reason && (
                                <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3" data-testid="kyc-review-rejection">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                                        <div className="text-sm">
                                            <strong>{t("offers.rejectionReason")}: </strong>{p.kyc_rejection_reason}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <KycDocumentsPanel
                                summary={data}
                                readOnly={false}
                                uploadFn={upload}
                                removeFn={remove}
                                onChange={refresh}
                                testIdPrefix="admin-kyc"
                            />

                            {isPending ? (
                                <div className="surface-card p-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-end sticky bottom-0 bg-card/95 backdrop-blur">
                                    <p className="text-xs text-muted-foreground sm:me-auto">{t("kyc.review.pendingHelp")}</p>
                                    <Button
                                        variant="outline"
                                        onClick={() => setDecision({ open: true, action: "REJECTED" })}
                                        className="text-destructive hover:bg-destructive/5"
                                        data-testid="kyc-review-reject"
                                    >
                                        <XCircle className="h-4 w-4 me-1" />
                                        {t("kyc.review.rejectAction")}
                                    </Button>
                                    <Button
                                        onClick={() => setDecision({ open: true, action: "APPROVED" })}
                                        data-testid="kyc-review-approve"
                                    >
                                        <CheckCircle2 className="h-4 w-4 me-1" />
                                        {t("kyc.review.approveAction")}
                                    </Button>
                                </div>
                            ) : (
                                <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground" data-testid="kyc-review-not-pending">
                                    {t("kyc.review.notPending")}
                                </div>
                            )}
                        </div>
                    ) : null}
                </SheetContent>
            </Sheet>

            <KycDecisionDialog
                open={decision.open}
                action={decision.action}
                onOpenChange={(o) => setDecision((d) => ({ ...d, open: o }))}
                onConfirm={(reason) => decide(decision.action, reason)}
                t={t}
                isRTL={isRTL}
            />
        </>
    );
}

function KycDecisionDialog({ open, action, onOpenChange, onConfirm, t, isRTL }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (open) { setReason(""); setSubmitting(false); } }, [open]);

    const isReject = action === "REJECTED";

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        try { await onConfirm(reason); } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md rounded-2xl"
                data-testid={isReject ? "kyc-reject-dialog" : "kyc-approve-dialog"}
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>{t(isReject ? "kyc.review.rejectTitle" : "kyc.review.approveTitle")}</DialogTitle>
                    <DialogDescription>
                        {t(isReject ? "kyc.review.rejectBody" : "kyc.review.approveBody")}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    {isReject && (
                        <div>
                            <Label htmlFor="kyc_reject_reason">{t("kyc.review.rejectReasonLabel")}</Label>
                            <Textarea
                                id="kyc_reject_reason"
                                rows={4}
                                required
                                placeholder={t("kyc.review.rejectReasonPlaceholder")}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="mt-1.5"
                                data-testid="kyc-reject-reason"
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
                        <Button
                            type="submit"
                            disabled={submitting || (isReject && !reason.trim())}
                            className={isReject ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                            data-testid={isReject ? "kyc-reject-confirm" : "kyc-approve-confirm"}
                        >
                            {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t(isReject ? "kyc.review.rejectAction" : "kyc.review.approveAction")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
